import TestimonialCard from '@/components/layout/home/testimonial-card';
import ScrollingContainer from '@/components/shared/scrolling-container';
import { Tag } from '@/components/ui/tag';
import { MOTION } from '@/utils';
import { TESTIMONIALS } from '@/utils/data/testimonials';
import { useGSAP } from '@gsap/react';
import { gsap } from 'gsap';
import { SplitText } from 'gsap/SplitText';
import { useRef } from 'react';

const rowClass =
  "relative w-full overflow-hidden before:absolute before:left-0 before:top-0 before:z-2 before:h-full before:w-[100px] before:md:w-[150px] before:bg-[linear-gradient(to_right,white_0%,rgba(255,255,255,0)_100%)] before:content-[''] after:absolute after:right-0 after:top-0 after:z-2 after:h-full after:w-[100px] after:md:w-[150px] after:bg-[linear-gradient(to_left,white_0%,rgba(255,255,255,0)_100%)] after:content-['']";

export default function Testimonials() {
  const refs = {
    section: useRef<HTMLElement>(null),
    tag: useRef(null),
    title: useRef(null),
    rows: useRef<HTMLDivElement[]>([]),
  };

  const setRowRef = (el: HTMLDivElement | null, index: number) => {
    if (el) refs.rows.current[index] = el;
  };

  useGSAP(
    () => {
      if (!refs.section.current || !refs.title.current) return;

      const splitTitle = new SplitText(refs.title.current, { mask: 'lines', type: 'lines' });
      const rows = refs.rows.current.filter(Boolean);

      gsap.set(refs.tag.current, { autoAlpha: 0, y: 8 });
      gsap.set(rows, { autoAlpha: 0, y: MOTION.y });

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: refs.section.current,
          start: 'top 60%',
          toggleActions: 'play none none reverse',
        },
      });

      tl.to(refs.tag.current, {
        autoAlpha: 1,
        y: 0,
        duration: MOTION.durationSm,
        ease: MOTION.ease,
      })
        .from(
          splitTitle.lines,
          {
            yPercent: MOTION.yPercent,
            stagger: MOTION.staggerLines,
            duration: MOTION.durationMd,
            ease: MOTION.ease,
          },
          '<+0.15',
        )
        .to(
          rows,
          {
            autoAlpha: 1,
            y: 0,
            duration: MOTION.durationMd,
            ease: MOTION.ease,
            stagger: 0.12,
          },
          '<+0.2',
        );

      return () => splitTitle.revert();
    },
    { scope: refs.section },
  );

  return (
    <section
      ref={refs.section}
      className="mx-auto flex flex-col items-start gap-12 p-0! py-16 md:px-0"
      id="clients"
    >
      <div className="max-width-white-section-content flex w-full flex-col gap-3 pb-0!">
        <Tag ref={refs.tag}>Ils nous font confiance</Tag>
        <h2 ref={refs.title} className='w-fit'>Témoignages</h2>
      </div>

      <div className="flex flex-col gap-5 pb-20">
        <div ref={(el) => setRowRef(el, 0)}>
          <ScrollingContainer className={rowClass} scrollSpeed={45}>
            <div className="flex gap-5 px-2.5">
              {TESTIMONIALS.map((item) => (
                <TestimonialCard
                  key={item.cabinet}
                  cabinet={item.cabinet}
                  client={item.client}
                  description={item.description}
                  logo={item.logo}
                />
              ))}
            </div>
          </ScrollingContainer>
        </div>

        <div ref={(el) => setRowRef(el, 1)}>
          <ScrollingContainer className={`${rowClass} -scale-x-100`} scrollSpeed={35}>
            <div className="flex gap-5 px-2.5">
              {TESTIMONIALS.map((item) => (
                <TestimonialCard
                  key={item.cabinet}
                  cabinet={item.cabinet}
                  client={item.client}
                  description={item.description}
                  logo={item.logo}
                  reverse
                />
              ))}
            </div>
          </ScrollingContainer>
        </div>
      </div>
    </section>
  );
}
